import React from "react";
import {
  Swords,
  Flame,
  Trophy,
  Users,
  Pin,
  Timer,
  PlayCircle,
  Check,
  BookOpenCheck,
  Share2,
  Crown,
  Sparkles,
  CalendarDays,
  FileText,
} from "lucide-react";
import Reveal from "../ui/Reveal";
import { useAppDownload } from "../../context/AppDownloadContext";
import "../../styles/home-stories.css";

/* Each mock is an illustration of a real in-app feature. Names and numbers are examples, not user data. */

function ChallengeMock() {
  const steps = ["Share link", "Ada joins", "Results"];
  return (
    <div className="story-phone story-phone--challenge" aria-hidden>
      <div className="story-phone-bar">
        <Swords size={16} /> Challenge a friend
      </div>

      <div className="story-meta">
        <span>Biology</span>
        <span>10 questions</span>
        <span>15 min</span>
      </div>

      <ol className="story-track">
        {steps.map((label, i) => (
          <li key={label} className="is-done">
            <span className="story-track-dot"><Check size={12} /></span>
            {label}
            {i < steps.length - 1 && <i className="story-track-line" />}
          </li>
        ))}
      </ol>

      <div className="story-versus">
        <div className="story-player story-player--win">
          <span className="story-winner-tag"><Crown size={12} /> Winner</span>
          <span className="story-avatar">Y</span>
          <b>You</b>
          <em>8<small>/10</small></em>
          <span className="story-bar"><i style={{ width: "80%" }} /></span>
        </div>
        <span className="story-vs">VS</span>
        <div className="story-player">
          <span className="story-avatar story-avatar--alt">A</span>
          <b>Ada</b>
          <em>7<small>/10</small></em>
          <span className="story-bar story-bar--alt"><i style={{ width: "70%" }} /></span>
        </div>
      </div>

      <div className="story-result">You won by 1 point</div>

      <div className="story-share-row">
        <span className="story-share-code">
          Code <strong>4K7Q2M</strong>
        </span>
        <span className="story-share-btn">
          <Share2 size={14} /> Send link
        </span>
      </div>
    </div>
  );
}

function DailyMock() {
  const steps = [
    { icon: FileText, label: "Read today's note", done: true },
    { icon: BookOpenCheck, label: "Take the daily quiz", done: true },
    { icon: PlayCircle, label: "Watch the explainer video", done: false },
  ];
  return (
    <div className="story-phone" aria-hidden>
      <div className="story-phone-bar">
        <Flame size={16} /> Today's daily test
      </div>
      <ul className="story-steps">
        {steps.map(({ icon: Icon, label, done }) => (
          <li key={label} className={done ? "is-done" : ""}>
            <span className="story-step-icon">{done ? <Check size={16} /> : <Icon size={16} />}</span>
            {label}
          </li>
        ))}
      </ul>
      <div className="story-streak-pill">
        <Flame size={18} /> Keep your streak going
      </div>
    </div>
  );
}

function StreakMock() {
  const days = ["M", "T", "W", "T", "F", "S", "S"];
  const rows = [
    ["1", "Tunde", "Chemistry"],
    ["2", "You", "Biology"],
    ["3", "Ada", "Physics"],
  ];
  return (
    <div className="story-phone" aria-hidden>
      <div className="story-phone-bar">
        <Trophy size={16} /> Streaks & leaderboard
      </div>
      <div className="story-week">
        {days.map((d, i) => (
          <div key={i} className={`story-day ${i < 5 ? "is-on" : ""}`}>
            {i < 5 ? <Flame size={16} /> : <span />}
            <small>{d}</small>
          </div>
        ))}
      </div>
      <ul className="story-board">
        {rows.map(([rank, name, subject]) => (
          <li key={rank} className={name === "You" ? "is-you" : ""}>
            <b>{rank}</b>
            <span>{name}</span>
            <em>{subject} streak</em>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CbtMock() {
  return (
    <div className="story-phone" aria-hidden>
      <div className="story-phone-bar story-phone-bar--split">
        <span>
          <BookOpenCheck size={16} /> JAMB CBT practice
        </span>
        <span className="story-timer">
          <Timer size={14} /> 41:20
        </span>
      </div>
      <p className="story-question">Which of these is a noble gas?</p>
      {["Nitrogen", "Argon", "Oxygen", "Chlorine"].map((o, i) => (
        <div key={o} className={`story-option ${i === 1 ? "is-picked" : ""}`}>
          <b>{"ABCD"[i]}</b> {o}
        </div>
      ))}
    </div>
  );
}

function ClassMock() {
  return (
    <div className="story-phone" aria-hidden>
      <div className="story-phone-bar">
        <Users size={16} /> Your programme group
      </div>
      <div className="story-pinned">
        <Pin size={14} />
        <span>
          <b>Pinned by your course rep</b>
          Exam timetable is out. Check the Timetable tab.
        </span>
      </div>
      <div className="story-bubble">Anyone has last year's past questions?</div>
      <div className="story-bubble story-bubble--me">Uploaded them to the group files.</div>
      <div className="story-chips">
        <span><CalendarDays size={12} /> Timetable</span>
        <span><FileText size={12} /> Past papers</span>
        <span><Crown size={12} /> Rep election</span>
      </div>
    </div>
  );
}

const stories = [
  {
    id: "challenge",
    eyebrow: "Challenge a friend",
    title: "Beat your friend, not just the syllabus.",
    text: "Pick a subject, choose how many questions and how long you get, then share a 6-digit code. Your friend joins, you both take the same test, and the score decides who's got bragging rights.",
    points: ["5 to 40 questions, 3 to 30 minutes", "Your friend joins with a 6-digit code", "See who won when you're both done"],
    Mock: ChallengeMock,
  },
  {
    id: "daily",
    eyebrow: "Daily test",
    title: "Ten minutes a day that actually sticks.",
    text: "Every day gets a short note, a quiz and a video. It's small enough to do on the bus and consistent enough to change your score.",
    points: ["A note, a quiz and an explainer video", "Built for a quick daily habit", "Feeds straight into your streak"],
    Mock: DailyMock,
    flip: true,
  },
  {
    id: "streaks",
    eyebrow: "Streaks & leaderboard",
    title: "Don't break the chain.",
    text: "Daily streaks and per-subject streaks make showing up feel like a game. Climb the leaderboard and see who's putting in the work.",
    points: ["Daily and subject streaks", "Weekly progress at a glance", "A leaderboard to chase"],
    Mock: StreakMock,
  },
  {
    id: "cbt",
    eyebrow: "JAMB & WAEC prep",
    title: "The real exam feel, before the real exam.",
    text: "Practise with past questions and a timed CBT simulator, check your admission eligibility and track how you're improving.",
    points: ["Past questions and CBT simulation", "Eligibility checker and syllabus", "Performance tracking over time"],
    Mock: CbtMock,
    flip: true,
  },
  {
    id: "class",
    eyebrow: "For university students",
    title: "Your class, finally in one organised place.",
    text: "Get matched to your programme and entry year. Your class elects a course rep who can pin announcements, share the exam timetable and keep past questions in one place. No more scattered group chats.",
    points: ["A group for your programme and set", "Elected course rep, pinned announcements", "Exam timetable and past papers shared once"],
    Mock: ClassMock,
  },
];

export default function FeatureStories() {
  const { openAppModal } = useAppDownload();

  return (
    <section className="section stories-section">
      <div className="container">
        <Reveal>
          <div className="section-heading section-heading-center">
            <span className="eyebrow">What makes it different</span>
            <h2 className="heading-2">More than a CBT app. It's where studying gets social.</h2>
            <p className="body-large">
              Past questions are the start. Here's what students actually open CampusGenie for.
            </p>
          </div>
        </Reveal>

        <div className="stories">
          {stories.map(({ id, eyebrow, title, text, points, Mock, flip }, index) => (
            <Reveal key={id} delay={0.05}>
              <div className={`story ${flip ? "story--flip" : ""}`}>
                <div className="story-copy">
                  <span className="story-eyebrow">{eyebrow}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                  <ul className="story-points">
                    {points.map((p) => (
                      <li key={p}>
                        <Check size={16} /> {p}
                      </li>
                    ))}
                  </ul>
                  {index === 0 && (
                    <button type="button" className="btn btn-primary" onClick={openAppModal}>
                      Try a challenge
                    </button>
                  )}
                </div>
                <div className="story-visual">
                  <div className="story-glow" />
                  <Mock />
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="story-ai-strip">
            <Sparkles size={18} />
            <span>
              Plus an AI tutor, AI writing tools, a study planner, GPA tracker and digital student ID, all in the same app.
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
